import React, { useMemo } from 'react';

import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { AriRoomRateStateRow } from '~/services/ReportingService';

import NamedId from './NamedId';

function formatMoney(amount: number | null, currency: string | null) {
  if (amount == null) {
    return '—';
  }
  return `${currency ? `${currency} ` : ''}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatTimestamp(value: string | null) {
  return value ? value.slice(0, 19).replace('T', ' ') : '—';
}

// A blank rate is usually routine, so only the real gap is flagged: no rate at
// any occupancy while a sibling rate plan on the room type does have one.
function renderRateAfterTax(row: AriRoomRateStateRow) {
  if (row.rate_after_tax != null) {
    if (hasUnidentifiedLos(row)) {
      return (
        <Tooltip title="Duration-priced, but no length of stay was identified upstream, so it is unclear how many nights this total covers">
          <Typography variant="body2" component="span" color="warning.main">
            {formatMoney(row.rate_after_tax, row.currency)}*
          </Typography>
        </Tooltip>
      );
    }
    if (isDurationPriced(row)) {
      return (
        <Tooltip title={`Total for a ${row.los_key}-night stay, not a nightly rate`}>
          <span>{formatMoney(row.rate_after_tax, row.currency)}</span>
        </Tooltip>
      );
    }
    return formatMoney(row.rate_after_tax, row.currency);
  }

  if (row.rate_plan_id == null) {
    return '—';
  }

  if (!row.has_rate_any_occupancy_flag && row.has_rate_any_rate_plan_flag) {
    return (
      <Tooltip title="No rate at any occupancy for this night, while another rate plan on the same room type does have one — the gap is specific to this rate plan">
        <Chip size="small" label="no rate" color="warning" variant="outlined" />
      </Tooltip>
    );
  }

  if (row.has_rate_any_occupancy_flag) {
    return (
      <Tooltip
        title={`Priced at another occupancy — highest is ${formatMoney(row.rate_after_tax_any_occupancy, row.currency)}`}
      >
        <span>—</span>
      </Tooltip>
    );
  }

  return '—';
}

// Duration pricing sends one total per length of stay, so a rate here covers
// los_key nights rather than one. los_key 0 means the band was not identified.
function isDurationPriced(row: AriRoomRateStateRow) {
  return row.rate_time_unit === 'duration';
}

function hasUnidentifiedLos(row: AriRoomRateStateRow) {
  return isDurationPriced(row) && row.los_key === 0;
}

export function anyDurationPriced(rows: Array<AriRoomRateStateRow>) {
  return rows.some(isDurationPriced);
}

interface Props {
  rows: Array<AriRoomRateStateRow>;
}

export default function AriStateTable({ rows }: Props) {
  // The API applies no ORDER BY, so rows arrive interleaved across rate plans.
  // Grouping keeps one product's LOS/occupancy buckets together.
  const sortedRows = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          (a.room_type_name ?? '').localeCompare(b.room_type_name ?? '') ||
          (a.rate_plan_name ?? '').localeCompare(b.rate_plan_name ?? '') ||
          (a.room_rate_id ?? '').localeCompare(b.room_rate_id ?? '') ||
          a.los_key - b.los_key ||
          a.occupancy_key - b.occupancy_key,
      ),
    [rows],
  );

  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Room type</TableCell>
            <TableCell>Rate plan</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>LOS</TableCell>
            <TableCell>Occupancy</TableCell>
            <TableCell align="right">Rate (after tax)</TableCell>
            <TableCell align="right">Rate (before tax)</TableCell>
            <TableCell align="right">Inventory</TableCell>
            <TableCell>Closed</TableCell>
            <TableCell align="right">Min / max stay</TableCell>
            <TableCell>Rate last set</TableCell>
            <TableCell>Inventory last set</TableCell>
            <TableCell align="right">Cells</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedRows.map((row) => (
            <TableRow
              key={`${row.source_service}-${row.room_type_id}-${row.room_rate_id ?? 'roomtype'}-${row.los_key}-${row.occupancy_key}`}
              hover
            >
              <TableCell>
                <NamedId name={row.room_type_name} id={row.room_type_id} />
              </TableCell>

              <TableCell>
                {row.rate_plan_id ? (
                  <NamedId name={row.rate_plan_name} id={row.rate_plan_id} />
                ) : (
                  <Tooltip title="Room-type grain — Synxis inventory (OTA_HotelInvCountNotifRQ) carries no rate plan, so this row belongs to the room type rather than any one rate">
                    <Chip size="small" label="whole room type" variant="outlined" />
                  </Tooltip>
                )}
              </TableCell>

              <TableCell>{row.source_service}</TableCell>

              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {hasUnidentifiedLos(row) ? (
                  <Tooltip title="Duration-priced, but the provider sent a length-of-stay range rather than a single band, so it could not be identified upstream">
                    <Chip size="small" label="LOS unknown" color="warning" variant="outlined" />
                  </Tooltip>
                ) : isDurationPriced(row) ? (
                  <Tooltip title="Duration priced — the rate is the total for a stay of this many nights">
                    <span>{`${row.los_key} night${row.los_key === 1 ? '' : 's'}`}</span>
                  </Tooltip>
                ) : row.los_key === 0 ? (
                  <Tooltip title="los_key 0 means nightly pricing, not a zero-night stay">
                    <span>Nightly</span>
                  </Tooltip>
                ) : (
                  row.los_key
                )}
              </TableCell>

              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {row.occupancy_key === 0 ? (
                  <Tooltip title="The source sent no occupancy. On Synxis this bucket mixes genuine any-occupancy rates with restriction-only rows, so an empty rate here does not mean unpriced">
                    <span>Unspecified</span>
                  </Tooltip>
                ) : (
                  row.occupancy_key
                )}
              </TableCell>

              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {renderRateAfterTax(row)}
              </TableCell>

              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {formatMoney(row.rate_before_tax, row.currency)}
              </TableCell>

              <TableCell align="right">{row.inventory ?? '—'}</TableCell>

              <TableCell>
                {row.closed_flag == null ? (
                  '—'
                ) : (
                  <Chip
                    size="small"
                    label={row.closed_flag ? 'Closed' : 'Open'}
                    color={row.closed_flag ? 'error' : 'success'}
                    variant="outlined"
                  />
                )}
              </TableCell>

              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {row.min_stay ?? '—'} / {row.max_stay ?? '—'}
              </TableCell>

              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(row.rate_last_set_at)}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(row.inventory_last_set_at)}</TableCell>
              <TableCell align="right">{row.cell_count ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
